import { get } from "lodash";
import { Item, ItemFields } from "constants/product-collection";
import { AllocationBagType, forEachAllocationBag, getAllocationBagValue } from "../../allocation-dimension";
import { safeParseFloat } from "lib/number";
import { generateDoorMap, getMetricValuesFromNewBagData, getMetricValuesFromOldBagData } from "./formula-helpers";
import { ValueGetterParams } from "ag-grid-community";

/**
 //  * Formulas for Metrics
 //  *
 //  * @param factoryOptions
 //  *  - provided to `metricsValueGetterCreator`
 //  *  - untyped, easily allow for custom inputs needed for calculation
 //  * @param getterParams
 //  *  - default getterParams from AgGrid
 //  *  - has context and data etc to facilitate calculation
 //  * @returns
 //  *  - a numerical caculated metrics value
 //  */
export const TOTAL_RETAIL = ({ factoryOptions, getterParams }) => {
    const { data, context, api, node }: ValueGetterParams = getterParams;
    const { allocationBagRef } = context;
    const { deliveryId, doors, useSize } = factoryOptions;
    const doorsMap = generateDoorMap(doors);

    const gridApi = factoryOptions.rootGridApi ?? api;
    let totalRetail = 0;
    /**
     * New allocation bag
     */
    if (allocationBagRef?.allocationBag) {
        const itemId = get(data, Item.FIELDS.ID);
        const allocationBag = allocationBagRef?.allocationBag;

        if (deliveryId && deliveryId !== "") {
            for (const door of doors) {
                let bulkCount = 0;
                if (useSize) {
                    const { size } = data;
                    bulkCount = getAllocationBagValue(AllocationBagType.allocations, allocationBag, itemId, [
                        deliveryId,
                        door.id,
                        size,
                    ]);
                } else {
                    bulkCount = getAllocationBagValue(AllocationBagType.bulks, allocationBag, itemId, [
                        deliveryId,
                        door.id,
                    ]);
                }
                if (door && bulkCount) {
                    let retail = door.retail.fieldName ? gridApi.getValue(door.retail.fieldName, node) : undefined;
                    if (!retail) {
                        retail = get(data, [ItemFields.Fields, door.retail.fieldName, "string_value"]);
                    }
                    const doorRetail = safeParseFloat(retail, 0);
                    totalRetail += bulkCount * doorRetail;
                }
            }
        } else {
            if (useSize) {
                const { size } = data;
                forEachAllocationBag(allocationBag, itemId, AllocationBagType.allocations, (dimensions, value) => {
                    const { shouldSkip, doorIdValue, bulkCount } = getMetricValuesFromNewBagData(
                        deliveryId,
                        doorsMap,
                        dimensions,
                        value,
                        useSize,
                        size
                    );

                    if (shouldSkip) {
                        return;
                    }

                    const door = doorsMap.get(doorIdValue);
                    if (door && bulkCount > 0) {
                        let retail = door.retail.fieldName ? gridApi.getValue(door.retail.fieldName, node) : undefined;
                        if (!retail) {
                            retail = get(data, [ItemFields.Fields, door.retail.fieldName, "string_value"]);
                        }
                        const doorRetail = safeParseFloat(retail, 0);
                        totalRetail += bulkCount * doorRetail;
                    }
                });
            } else {
                forEachAllocationBag(allocationBag, itemId, AllocationBagType.bulks, (dimensions, value) => {
                    const { shouldSkip, doorIdValue, bulkCount } = getMetricValuesFromNewBagData(
                        deliveryId,
                        doorsMap,
                        dimensions,
                        value,
                        false
                    );

                    if (shouldSkip) {
                        return;
                    }

                    const door = doorsMap.get(doorIdValue);
                    if (door && bulkCount > 0) {
                        let retail = door.retail.fieldName ? gridApi.getValue(door.retail.fieldName, node) : undefined;
                        if (!retail) {
                            retail = get(data, [ItemFields.Fields, door.retail.fieldName, "string_value"]);
                        }
                        const doorRetail = safeParseFloat(retail, 0);
                        totalRetail += bulkCount * doorRetail;
                    }
                });
            }
        }
    } else {
        /**
         * Old allocation bag
         */
        let cols: string[][] = [];
        const size: string = data.size;
        if (data.allocations && data.allocations.current) {
            cols = data.allocations.current.r.map((r) => r.c);
        } else if (data.bulks && data.bulks.r) {
            cols = data.bulks.r.map((r) => r.c);
        } else if (data.bulks && data.bulks.current && data.bulks.current.r) {
            cols = data.bulks.current.r.map((r) => r.c);
        }

        cols.forEach((col) => {
            const { doorIdValue, bulkCount, shouldSkip } = getMetricValuesFromOldBagData(
                deliveryId,
                doorsMap,
                col,
                useSize,
                size
            );

            if (shouldSkip) {
                return;
            }

            const door = doorsMap.get(doorIdValue);
            if (door && bulkCount > 0) {
                let retail = door.retail.fieldName ? gridApi.getValue(door.retail.fieldName, node) : undefined;
                if (!retail) {
                    retail = get(data, [ItemFields.Fields, door.retail.fieldName, "string_value"]);
                }
                const doorRetailCost = safeParseFloat(retail, 0);
                totalRetail += bulkCount * doorRetailCost;
            }
        });
    }

    if (totalRetail === 0) {
        return undefined;
    }

    return totalRetail;
};
